import { LayoutBaseProps } from "@/atoms-react/layout-base/LayoutBase.react";
import {
  REQUIRED_SUBSTEPS_AT_CONNECTION,
  REQUIRED_SUBSTEPS_AT_CONNECTION_LV2
} from "@/features/flow/constants.ts";
import { asSubstep, DeclarativeIdentity, DeclarativeNif } from "@/features/flow/types";
import {
  getNextSubstep,
  getStepMap,
  isDeclarativeMonthlyIncomSourceValid
} from "@/features/register/utils.ts";
import { Routes } from "@/features/router";
import { FullCountry, NaturalPerson } from "@/features/user/types.ts";
import { isDeclarativePersonValid } from "@/features/utils/declarative_identity_person.ts";
import { useAppDispatch, useAppSelector } from "@/redux/hooks.ts";
import { setSubstepNamesOfMissingFields } from "@/redux/slices/missingFieldsSlice.ts";
import {
  setDeclarativeIdentityAddressBirth,
  setDeclarativeIdentityAddressResidence,
  setDeclarativeIdentityMonthlyIncome,
  setDeclarativeIdentityOccupation,
  setDeclarativeIdentityPerson,
  setDeclarativeIdentityPersonNationalityCodes,
  setDeclarativeIdentitySourcesIncome,
  setDeclarativeIdentityTaxAdress,
  setDeclarativeIdentityWelcome,
  SIGNUP_SLICE_INITIAL
} from "@/redux/slices/signupSlice.ts";
import {
  getRequiredConnectionKeysFlow,
  isDeclarativeBirthValid,
  isDeclarativeMonthlyIncomeValid,
  isDeclarativeNationalityValid,
  isDeclarativeNifValid,
  isDeclarativeOccupationValid,
  isDeclarativeResidenceValid
} from "@/utils/declarative.utils.ts";
import { getRoute, OlkyPassRoutes } from "@/utils/routes";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { animatedNext } from "../../../layout/AnimatedForm.react";
import { CreatingAccount } from "../creating-account/index.react.tsx";
import DeclarativeIdentityBirth from "./birth/DeclarativeIdentityBirth.react";
import DeclarativeIdentityMonthlyIncome from "./monthly-income/DeclarativeIdentityMonthlyIncome.react.tsx";
import DeclarativeNationality from "./nationality/DeclarativeIdentityPerson.react.tsx";
import DeclarativeNifTab from "./nif/DeclarativeNif.react.tsx";
import DeclarativeIdentityOccupation from "./occupation/DeclarativeIdentityOccupation.react";
import DeclarativeIdentityPerson from "./person/DeclarativeIdentityPerson.react";
import DeclarativeIdentityResidence from "./residence/DeclarativeIdentityResidence.react";
import { DeclarativeSourceIncomeStep } from "./source-income/source-income-step.react.tsx";

interface DeclarativeIdentityRootProps extends LayoutBaseProps {
  input: DeclarativeIdentityInput;
  onValidated?: (data: DeclarativeIdentityOutput) => void;
  countries: FullCountry[];
  firstSubstep?: string; //in the "missing fields" flow we sometimes start at another substep
}

export type DeclarativeIdentityInput = {
  email: string;
  phone: string;
};
export type DeclarativeIdentityOutput = DeclarativeIdentity;

export type DeclarativeIdentityUIState = {
  sawWelcomeMessage: boolean;
};

function DeclarativeIdentityRoot({
  onValidated = () => {},
  countries,
  firstSubstep
}: DeclarativeIdentityRootProps) {
  const [profile, setProfile] = useState<NaturalPerson | null>(null);
  const [missingKeys, setMissingKeys] = useState({});
  const { block, session_uuid, step, substep } = useParams();
  const navigate = useNavigate();
  const substepsOfMissingfields = useAppSelector((state) => state.missingFields.substeps);
  const isLoading = useAppSelector((state) => state.flow.isLoading);
  const mirror_uuid = useAppSelector((state) => state.signup.mirror_uuid);
  const tempUuid = sessionStorage.getItem("temp_uuid_real");

  const dispatch = useAppDispatch();
  const olkypassLevel = useAppSelector((state) => state.olkypass.level);
  const signupState = useAppSelector((state) => state.signup);
  const declarativeIdentity = signupState.identity ?? {};
  const prioritySections = [
    "person",
    "birth",
    "nationality",
    "residence",
    "nif",
    "activity",
    "monthly_income",
    "source_income"
  ]; // Define the priority order
  useEffect(() => {
    const storedValue = sessionStorage.getItem("NewOlkyPayUser");
    if (storedValue && signupState === SIGNUP_SLICE_INITIAL) {
      const jsonItems = JSON.parse(storedValue);
      const accountProfile = jsonItems.profile.naturalPerson;
      const missingFields = jsonItems.missingKeys;
      setProfile(accountProfile);
      if (block === "required_field") {
        //in the "required_field" flow we only care about
        //a sub-selection of missing fields
        const substeps =
          olkypassLevel > 1 ? REQUIRED_SUBSTEPS_AT_CONNECTION_LV2 : REQUIRED_SUBSTEPS_AT_CONNECTION;
        for (const missingFieldSubstep of Object.keys(missingFields)) {
          if (!substeps.includes(missingFieldSubstep)) delete missingFields[missingFieldSubstep];
        }
        dispatch(setSubstepNamesOfMissingFields(Object.keys(missingFields)));
      }
      setMissingKeys(missingFields);
      if (!substep) {
        let mySubStep = substep;
        for (const section of Object.keys(missingFields)) {
          const sectionName = section.split(".").pop();
          if (prioritySections.includes(sectionName as string)) {
            //this defines the missing fields steps
            mySubStep = sectionName;
            break;
          }
        }
        if ((!mySubStep || Object.keys(missingFields).length === 0) && block === "required_field") {
          navigate(OlkyPassRoutes.dashboard);
        } else {
          navigate(
            getRoute(Routes.flow_substep, {
              session_uuid,
              block,
              step,
              substep: mySubStep
            }),
            {
              replace: true
            }
          );
        }
      }
    } else if (!session_uuid || !substep || !declarativeIdentity?.welcome) {
      if (firstSubstep)
        animatedNext("forward", async () => {
          navigate(
            getRoute(Routes.flow_substep, {
              session_uuid,
              block,
              step,
              substep: firstSubstep
            })
          );
        });
      else {
        // Redirect to welcome if invalid block or just reloaded the page now
        dispatch(setDeclarativeIdentityWelcome());
        navigate(getRoute(Routes.flow_substep, { session_uuid, block, step, substep: "person" }), {
          replace: true
        });
      }
    }
  }, []);
  const validationFunctions = {
    person: isDeclarativePersonValid,
    birth: isDeclarativeBirthValid,
    nationality: isDeclarativeNationalityValid,
    residence: isDeclarativeResidenceValid,
    activity: isDeclarativeOccupationValid,
    monthly_income: isDeclarativeMonthlyIncomeValid,
    source_income: isDeclarativeMonthlyIncomSourceValid,
    nif: isDeclarativeNifValid
  };
  const equivalentFields = {
    person: "person",
    birth: "addressBirth",
    addressBirth: "residence",
    residence: "addressResidence",
    activity: "occupation",
    nif: "taxAdress",
    nationality: "person",
    monthly_income: "monthlyIncome",
    source_income: "sourcesIncome"
  };

  useEffect(() => {
    if (block !== "required_field") {
      //signup flow
      if (profile && Object.keys(missingKeys).length > 0) {
        let isDeclarativeIdentityAuxCompleted = true;
        for (const section of Object.keys(missingKeys)) {
          const sectionName = section.split(".").pop();
          if (sectionName) {
            const equivalentKey = equivalentFields[sectionName];
            const value = declarativeIdentity[equivalentKey] ?? null;
            const validationFunction = validationFunctions[sectionName];
            // Validate the section dynamically
            if (validationFunction) {
              const isValid = validationFunction(value);
              if (!isValid) isDeclarativeIdentityAuxCompleted = false;
            }
          }
        }
        if (isDeclarativeIdentityAuxCompleted && declarativeIdentity)
          onValidated(declarativeIdentity);
      }
    } else if (profile && signupState !== SIGNUP_SLICE_INITIAL) {
      const updatedMissingKeys = getRequiredConnectionKeysFlow(
        countries,
        signupState,
        profile,
        olkypassLevel
      );
      setMissingKeys(updatedMissingKeys);
    }
  }, [declarativeIdentity]);

  const calculateNextSubStep = (mySubStep: string | null) => {
    if (mySubStep === null) return null;

    const nextStep =
      block === "required_field"
        ? getNextSubstep(block, substepsOfMissingfields, asSubstep(substep))
        : getStepMap()[mySubStep];
    return nextStep;
  };

  if (isLoading) return <CreatingAccount />;

  return (
    <>
      {substep === "person" && (
        <DeclarativeIdentityPerson
          profile={profile}
          declarativePerson={declarativeIdentity.person}
          onValidated={(data) =>
            animatedNext("forward", async () => {
              dispatch(setDeclarativeIdentityPerson(data));
              navigate(
                getRoute(Routes.flow_substep, {
                  session_uuid,
                  block,
                  step,
                  substep: calculateNextSubStep("person")
                })
              );
            })
          }
        />
      )}
      {substep === "nationality" && (
        <DeclarativeNationality
          signupStateNationalities={declarativeIdentity.person?.nationalityCodes}
          profile={profile}
          onValidated={(data) =>
            animatedNext("forward", async () => {
              dispatch(setDeclarativeIdentityPersonNationalityCodes(data.nationalityCodes));
              navigate(
                getRoute(Routes.flow_substep, {
                  session_uuid,
                  block,
                  step,
                  substep: calculateNextSubStep("nationality")
                })
              );
            })
          }
        />
      )}
      {substep === "nif" && (
        <DeclarativeNifTab
          declarativePerson={declarativeIdentity.addressBirth}
          personNif={declarativeIdentity.taxAdress}
          onValidated={(data: DeclarativeNif[]) =>
            animatedNext("forward", async () => {
              dispatch(setDeclarativeIdentityTaxAdress(data));
              navigate(
                getRoute(Routes.flow_substep, {
                  session_uuid,
                  block,
                  step,
                  substep: calculateNextSubStep("nif")
                })
              );
            })
          }
        />
      )}
      {substep === "birth" && (
        <DeclarativeIdentityBirth
          declarativeBirth={declarativeIdentity.addressBirth}
          personNationality={declarativeIdentity.person?.nationalityCodes}
          profile={profile}
          onValidated={(data) =>
            animatedNext("forward", async () => {
              dispatch(setDeclarativeIdentityAddressBirth(data));
              navigate(
                getRoute(Routes.flow_substep, {
                  session_uuid,
                  block,
                  step,
                  substep: calculateNextSubStep("birth")
                })
              );
            })
          }
        />
      )}
      {substep === "residence" && (
        <DeclarativeIdentityResidence
          declarativeResidence={declarativeIdentity.addressResidence ?? {}}
          onValidated={(data) =>
            animatedNext("forward", async () => {
              dispatch(setDeclarativeIdentityAddressResidence(data));
              navigate(
                getRoute(Routes.flow_substep, {
                  session_uuid,
                  block,
                  step,
                  substep: calculateNextSubStep("residence")
                })
              );
            })
          }
        />
      )}
      {substep === "activity" && (
        <DeclarativeIdentityOccupation
          declarativeOccupation={declarativeIdentity.occupation}
          onValidated={(data) =>
            animatedNext("forward", async () => {
              dispatch(setDeclarativeIdentityOccupation(data));
              navigate(
                getRoute(Routes.flow_substep, {
                  session_uuid,
                  block,
                  step,
                  substep: calculateNextSubStep("activity")
                })
              );
            })
          }
        />
      )}

      {substep === "monthly_income" && (
        <DeclarativeIdentityMonthlyIncome
          declarativeMonthlyIncome={declarativeIdentity.monthlyIncome}
          onValidated={(data) =>
            animatedNext("forward", async () => {
              dispatch(setDeclarativeIdentityMonthlyIncome(data));
              navigate(
                getRoute(Routes.flow_substep, {
                  session_uuid,
                  block,
                  step,
                  substep: calculateNextSubStep("monthly_income")
                })
              );
            })
          }
        />
      )}

      {substep === "source_income" && (
        <DeclarativeSourceIncomeStep
          declarativeSourceIncome={declarativeIdentity.sourcesIncome}
          onValidated={(data) => {
            if (block === "required_field" && tempUuid === null && mirror_uuid === null) {
              animatedNext("forward", async () => {
                dispatch(setDeclarativeIdentitySourcesIncome(data));
                navigate(
                  getRoute(Routes.flow_substep, {
                    session_uuid,
                    block,
                    step,
                    substep: calculateNextSubStep("source_income")
                  })
                );
              });
            } else {
              animatedNext("forward", async () => {
                dispatch(setDeclarativeIdentitySourcesIncome(data));
                onValidated({ ...declarativeIdentity, sourcesIncome: data });
              });
            }
          }}
        />
      )}
    </>
  );
}

export default DeclarativeIdentityRoot;
